// src/components/TermsAndConditions.js
import React from 'react';
import { FaExclamationTriangle, FaUserShield, FaLanguage, FaBalanceScale } from 'react-icons/fa';
import { MdSecurity, MdContentCopy, MdUpdate, MdContactMail } from 'react-icons/md';
import SEO from './SEO';

const TermsAndConditions = () => {
  return (
    <div className="container py-5" style={{ maxWidth: '800px' }}>
      <SEO
        title="Terms and Conditions - My Exam World"
        description="Read the terms and conditions governing the use of My Exam World online testing platform."
        url="/terms-and-conditions"
      />
      <h1 className="text-center mb-4 fw-bold">Terms and Conditions</h1>
      <div className="alert alert-info mb-4">
        <FaExclamationTriangle className="me-2" />
        <strong>Important:</strong> These terms govern your use of My Exam World. By accessing our platform, you agree to comply with all provisions below.
      </div>

      <TermSection 
        icon={<MdSecurity size={24} className="text-primary" />}
        title="1. Acceptance of Terms"
        content="By registering or using My Exam World, you agree to be legally bound by these terms. If you disagree with any part, you must immediately cease using our services."
      />

      <TermSection 
        icon={<FaUserShield size={24} className="text-success" />}
        title="2. User Accounts & Responsibility"
        content={
          <>
            <p>You are responsible for:</p>
            <ul>
              <li>Maintaining account confidentiality</li>
              <li>All activities under your account</li>
              <li>Immediately reporting unauthorized access</li>
            </ul>
          </>
        }
      />

      <TermSection 
        icon={<MdContentCopy size={24} className="text-warning" />}
        title="3. Content Ownership & Copyright"
        content={
          <>
            <p><strong>Creator Responsibility:</strong> Users who upload content affirm that:</p>
            <ul>
              <li>They own or have legal rights to all uploaded materials</li>
              <li>Content does not infringe any third-party rights</li>
              <li>They will be solely responsible for any copyright violations</li>
            </ul>
            <p className="mt-2">
              My Exam World may remove infringing content without notice and reserves the right to terminate repeat infringers' accounts.
            </p>
          </>
        }
      />

      <TermSection 
        icon={<FaBalanceScale size={24} className="text-danger" />}
        title="4. Permitted Use"
        content={
          <ul>
            <li>No misuse or interference with platform services</li>
            <li>No harmful, abusive, or misleading content</li>
            <li>No unauthorized commercial use</li>
            <li>No scraping or automated content extraction</li>
          </ul>
        }
      />

      <TermSection 
        icon={<FaLanguage size={24} className="text-info" />}
        title="5. Language Disclaimer"
        content="While we support multiple languages, machine translations may contain inaccuracies. My Exam World is not liable for consequences arising from translation errors."
      />

      <TermSection 
        icon={<FaExclamationTriangle size={24} className="text-danger" />}
        title="6. Termination Policy"
        content="We reserve the right to suspend or terminate accounts violating these terms immediately, without prior notice, at our sole discretion."
      />

      <TermSection 
        icon={<FaBalanceScale size={24} className="text-secondary" />}
        title="7. Liability Limitations"
        content="My Exam World shall not be liable for any indirect, incidental, or consequential damages arising from platform use, including but not limited to data loss or exam interruptions."
      />

      <TermSection 
        icon={<MdUpdate size={24} className="text-primary" />}
        title="8. Terms Modifications"
        content="We may update these terms periodically. Continued use after changes constitutes acceptance of revised terms. We recommend regular review of this page."
      />

      <TermSection 
        icon={<MdContactMail size={24} className="text-success" />}
        title="9. Contact Information"
        content={
          <p>
            For questions regarding these Terms, contact our legal team at <br />
            <a href="mailto:myexamworld1@gmail.com" className="text-decoration-none">myexamworld1@gmail.com</a>
          </p>
        }
      />

      <div className="card mt-4 border-light">
        <div className="card-body text-muted">
          <p className="mb-0"><strong>Last Updated:</strong> May 6, 2025</p>
          <p className="mb-0"><small>© 2025 My Exam World. All rights reserved.</small></p>
        </div>
      </div>
    </div>
  );
};

// Reusable Term Section Component
const TermSection = ({ icon, title, content }) => (
  <div className="mb-4 p-3 border-bottom">
    <div className="d-flex align-items-center mb-2">
      <span className="me-3">{icon}</span>
      <h4 className="mb-0">{title}</h4>
    </div>
    <div className="ps-5">
      {typeof content === 'string' ? <p>{content}</p> : content}
    </div>
  </div>
);

export default TermsAndConditions;